import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { shellCopy, threadCopy } from '@/copy'
import { Breadcrumbs, type Crumb } from '@/ui/Breadcrumbs/Breadcrumbs'
import { Button } from '@/ui/Button/Button'
import { Band } from '@/ui/Console/Console'
import { Icon } from '@/ui/Icon/Icon'
import { IconButton } from '@/ui/IconButton/IconButton'
import { useEscapeShortcut } from '@/ui/overlay/escapeStack'
import { messagesApi } from '../../api/messages'
import { ROUTES } from '../../constants/routes'
import { topicQueryKeys, useThreadsQuery } from '../../hooks/queries'
import { persistStorage } from '../../store/storage'
import { useToastStore } from '../../store/toast'
import { useWorkspaceStore } from '../../store/workspace'
import type {
  AgentParticipant,
  CreateMessageBody,
  MessageAcceptanceResponse,
} from '../../types/api'
import { newClientMessageId } from '../../utils/clientMessageId'
import { threadScopedRows } from '../../utils/threadResources'
import { useDocumentTitle } from '../useDocumentTitle'
import { ConsoleComposer } from './ConsoleComposer'
import styles from './ConsoleLayout.module.css'
import { useBandMenu, useFrame } from './frameContext'
import { ThreadResolvePrompt } from './ThreadResolvePrompt'
import { ThreadViewPane } from './ThreadViewPane'
import { TopicView } from './TopicView'

interface Props {
  topicId: string
  topicName?: string
  workspaceId?: string
  agents: AgentParticipant[]
  users: string[]
  currentUser: string | null
}

// ── Last-seen tracking ("While you were away" digest) ──────────────────────
// Per-topic timestamp of the user's last visit to the thread list. Persisted
// under the historical key (with its aframe-* legacy adopted) so the digest
// banner survives reloads; `entered` is a session-only snapshot.

const LAST_SEEN_KEY = 'chromaframe-last-seen'
const lastSeenStorage = persistStorage(LAST_SEEN_KEY, ['aframe-last-seen'])

function loadLastSeen(): Record<string, number> {
  const value = lastSeenStorage?.getItem(LAST_SEEN_KEY)
  if (!value || value instanceof Promise) return {}
  const state = value.state as { lastSeen?: Record<string, number> } | undefined
  return state?.lastSeen ?? {}
}

interface LastSeenState {
  lastSeen: Record<string, number>
  entered: Record<string, number>
}

/**
 * Single-pane console (redesign): flat rail on the left, the topic view
 * (agent header + board/resources) or the zoomed thread on the right —
 * never both — with the composer pinned bottom. Esc zooms out of an
 * open thread.
 */
export function ConsoleLayout({
  topicId,
  topicName,
  workspaceId,
  agents,
  users,
  currentUser,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams()

  // URL is the canonical source of truth, but we maintain an urgent local state
  // to drive the UI instantly. This prevents the websocket (urgent updates) from
  // interrupting the router's transition (low priority) and flashing the list.
  const [openThreadId, setOpenThreadId] = useState<string | null>(
    searchParams.get('thread') ?? null,
  )

  // Active TopicView tab — the composer renders on the threads tab only
  // (design §3).
  const [topicTab, setTopicTab] = useState<'threads' | 'resources'>(
    searchParams.get('tab') === 'resources' ? 'resources' : 'threads',
  )

  // Sync URL -> local state (e.g. user hits back button). Adjusted during
  // render rather than in an effect so there's no paint of the stale open
  // thread.
  const [lastSearch, setLastSearch] = useState(searchParams)
  if (searchParams !== lastSearch) {
    setLastSearch(searchParams)
    setOpenThreadId(searchParams.get('thread') ?? null)
    setTopicTab(
      searchParams.get('tab') === 'resources' ? 'resources' : 'threads',
    )
  }
  // Post-resolve prompt: keep/promote/archive the resolved thread's
  // scoped resources.
  const [resolvePrompt, setResolvePrompt] = useState<{
    threadId: string
    rows: import('../../types/api').TopicResource[]
  } | null>(null)
  const addToast = useToastStore((s) => s.addToast)
  const queryClient = useQueryClient()
  const workspaces = useWorkspaceStore((s) => s.workspaces)
  const workspaceName = workspaces.find(
    (w) => w.workspace_id === workspaceId,
  )?.name

  const [seen, setSeen] = useState<LastSeenState>(() => ({
    lastSeen: loadLastSeen(),
    entered: {},
  }))
  const seenBefore = seen.entered[topicId]
  const [topicMountTime, setTopicMountTime] = useState(() => Date.now())

  // Persist the last-seen map under the historical key whenever it changes.
  useEffect(() => {
    lastSeenStorage?.setItem(LAST_SEEN_KEY, {
      state: { lastSeen: seen.lastSeen },
      version: 1,
    })
  }, [seen.lastSeen])

  useEffect(() => {
    // Snapshot the previous visit's timestamp, then record this visit.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- topic-entry/leave timestamps must ride the mount lifecycle
    setSeen((s) => ({
      entered: { ...s.entered, [topicId]: s.lastSeen[topicId] ?? 0 },
      lastSeen: { ...s.lastSeen, [topicId]: Date.now() },
    }))
    setTopicMountTime(Date.now())
    return () =>
      setSeen((s) => ({
        ...s,
        lastSeen: { ...s.lastSeen, [topicId]: Date.now() },
      }))
  }, [topicId])

  const threadOpen = !!openThreadId

  const setOpenThread = useCallback(
    (id: string | null) => {
      // Urgent UI update
      setOpenThreadId(id)
      // Background URL sync
      setSearchParams(
        (prev) => {
          if (id) prev.set('thread', id)
          else prev.delete('thread')
          return prev
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const selectThread = useCallback(
    (id: string) => setOpenThread(id),
    [setOpenThread],
  )
  const closeThread = useCallback(() => setOpenThread(null), [setOpenThread])

  // `esc` zooms out of the open thread, unless an overlay or a focused field
  // took the key.
  useEscapeShortcut(closeThread, threadOpen)

  // The composer's scope decides whether a send starts a new thread (topic
  // scope) or replies in the open thread.
  const send = useCallback(
    // A failed send is reported once, by the Composer's own Alert (D142).
    async (body: CreateMessageBody): Promise<MessageAcceptanceResponse> => {
      const acceptance = await messagesApi.send(topicId, body)

      // Optimistically insert the new thread into the list cache so the zoomed
      // view has a title/preview instantly, avoiding an empty-title flash.
      if (!body.thread_id && acceptance.thread_id) {
        const preview = body.content.slice(0, 100)

        // Seed the thread list
        queryClient.setQueryData<import('../../types/api').ListThreadsResponse>(
          ['threads', topicId, 'active'],
          (old) => {
            if (!old) return old
            return {
              ...old,
              threads: [
                {
                  thread_id: acceptance.thread_id,
                  title: '',
                  root_preview: preview,
                  title_source: 'auto',
                  retitle_count: 0,
                  status: 'open',
                  needs_you: false,
                  reply_count: 0,
                  message_count: 1,
                  tool_count: 0,
                  agent_count: 0,
                  last_active_at: Date.now(),
                  participants: currentUser
                    ? [
                        {
                          participant_id: currentUser,
                          kind: 'user',
                          display_id: currentUser,
                        },
                      ]
                    : [],
                },
                ...old.threads,
              ],
            }
          },
        )

        // Seed the thread messages to prevent the "loading..." flash
        queryClient.setQueryData<
          import('../../types/api').FetchMessagesResponse
        >(['thread', topicId, acceptance.thread_id], {
          topic_id: topicId,
          messages: [
            {
              id: acceptance.message_id,
              author: {
                participant_id: currentUser ?? 'user',
                kind: 'user',
                display_id: currentUser ?? 'user',
              },
              role: 'user',
              content: body.content,
              timestamp: Date.now(),
              thread_id: acceptance.thread_id,
            },
          ],
        })
      }

      if (acceptance.joined_agent_ids.length > 0) {
        void queryClient.invalidateQueries({
          queryKey: topicQueryKeys.detail(topicId),
        })
      }
      if (acceptance.delivery_warning) {
        addToast(acceptance.delivery_warning, 'info')
      }
      if (!body.thread_id && acceptance.thread_id) {
        setOpenThread(acceptance.thread_id)
      }
      return acceptance
    },
    [addToast, queryClient, topicId, setOpenThread, currentUser],
  )

  // Cancel is topic-scoped server-side (cancels every active agent turn in the
  // topic), so this is unconditional on which thread is open.
  const cancelTurns = useCallback(async () => {
    try {
      await messagesApi.cancel(topicId)
    } catch (err) {
      addToast(
        err instanceof Error ? err.message : threadCopy.composer.stopFailed,
        'error',
      )
    }
  }, [addToast, topicId])

  const threadAction = useCallback(
    (fn: () => Promise<unknown>, failLabel: string, after?: () => void) => {
      void (async () => {
        try {
          await fn()
          after?.()
        } catch (err) {
          addToast(err instanceof Error ? err.message : failLabel, 'error')
        }
      })()
    },
    [addToast],
  )

  // Resolve, then (console-v2) prompt for the thread's scoped resources —
  // keep / promote / archive per row. A resolve failure surfaces through
  // the shared toast path; a cold resources cache means no prompt (the
  // safe default is leaving the rows thread-scoped).
  const resolveThreadWithPrompt = useCallback(
    (threadId: string) => {
      void (async () => {
        try {
          await messagesApi.resolveThread(topicId, threadId)
          void queryClient.invalidateQueries({ queryKey: ['threads', topicId] })
          void queryClient.invalidateQueries({ queryKey: ['waiting'] })
          const rows = threadScopedRows(queryClient, topicId, threadId)
          if (rows.length > 0) setResolvePrompt({ threadId, rows })
        } catch (err) {
          addToast(
            err instanceof Error ? err.message : threadCopy.view.resolveFailed,
            'error',
          )
        }
      })()
    },
    [topicId, queryClient, addToast],
  )

  return (
    <div className={styles.console}>
      <Breadcrumb
        topicId={topicId}
        topicName={topicName}
        workspaceName={workspaceName}
        openThreadId={openThreadId}
      />
      {threadOpen && openThreadId ? (
        <ThreadViewPane
          topicId={topicId}
          threadId={openThreadId}
          workspaceId={workspaceId}
          agents={agents}
          users={users}
          currentUser={currentUser}
          topicName={topicName}
          onClose={closeThread}
          onOpenTopicResources={() => {
            closeThread()
            setTopicTab('resources')
          }}
          onResolve={() => resolveThreadWithPrompt(openThreadId)}
          onReopen={() =>
            threadAction(
              () => messagesApi.reopenThread(topicId, openThreadId),
              threadCopy.view.reopenFailed,
            )
          }
          onArchive={() =>
            threadAction(
              () => messagesApi.archiveThreads(topicId, [openThreadId]),
              threadCopy.view.archiveFailed,
              closeThread,
            )
          }
          onContinue={() => {
            const body: CreateMessageBody = {
              content: 'Continue.',
              client_message_id: newClientMessageId(),
              mention_ids: [],
              thread_id: openThreadId,
            }
            // Not the Composer's send, so its Alert never shows: toast it.
            void send(body).catch((err) =>
              addToast(
                err instanceof Error
                  ? err.message
                  : threadCopy.composer.sendFailed,
                'error',
              ),
            )
          }}
        />
      ) : (
        // The redesigned topic view: agent header + board + resources
        // tab. Tab state is owned here so the thread panel's "all
        // topic resources" link can deep-link into it.
        <TopicView
          topicId={topicId}
          topicName={topicName}
          workspaceId={workspaceId}
          agents={agents}
          currentUser={currentUser}
          onSelect={selectThread}
          seenBefore={seenBefore}
          topicMountTime={topicMountTime}
          tab={topicTab}
          onTabChange={setTopicTab}
        />
      )}
      <ConsoleComposer
        key={topicId}
        hidden={!threadOpen && topicTab === 'resources'}
        topicId={topicId}
        openThreadId={openThreadId}
        topicName={topicName}
        onSend={send}
        onCancel={() => void cancelTurns()}
        users={users}
        workspaceId={workspaceId}
      />
      {resolvePrompt && (
        <ThreadResolvePrompt
          topicId={topicId}
          rows={resolvePrompt.rows}
          onClose={() => setResolvePrompt(null)}
        />
      )}
    </div>
  )
}

/** The console band: `workspace / topic / current` trail, Menu, topic settings. */
function Breadcrumb({
  topicId,
  topicName,
  workspaceName,
  openThreadId,
}: {
  topicId: string
  topicName?: string
  workspaceName?: string
  openThreadId: string | null
}) {
  const { openRail } = useFrame()
  useBandMenu()
  const { data } = useThreadsQuery(topicId)
  const openThread = openThreadId
    ? data?.threads.find((t) => t.thread_id === openThreadId)
    : undefined
  const tail = openThread
    ? openThread.title || openThread.root_preview
    : undefined
  useDocumentTitle(tail ?? topicName ?? '')
  const crumbs: Crumb[] = [
    {
      label: (workspaceName ?? shellCopy.band.workspaceFallback).toLowerCase(),
      href: ROUTES.home,
    },
    { label: topicName ?? '', href: tail ? ROUTES.topic(topicId) : undefined },
    ...(tail ? [{ label: tail }] : []),
  ]
  return (
    <Band
      lead={
        <Button variant="quiet" onClick={openRail}>
          {shellCopy.menu}
        </Button>
      }
      actions={
        <IconButton
          label={shellCopy.band.topicSettings}
          href={ROUTES.topicSettings(topicId)}
        >
          <Icon name="gear" />
        </IconButton>
      }
    >
      <Breadcrumbs crumbs={crumbs} />
    </Band>
  )
}
